import _isEqual from 'lodash/isEqual';
import React, { useState, useEffect } from 'react';
import { Modal } from 'src/components';
import Button from '../button';
import { BsPlusLg } from 'react-icons/bs';
import { BiMinus } from 'react-icons/bi';
import Checkbox from '@mui/material/Checkbox';
import { Slider as SliderMUI, ThemeProvider } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { getSpiceText } from 'src/utils/getSpiceValue';
import fire from 'src/assets/images/fire.svg';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { RootState } from 'src/redux/reducers';
import {
  addCurrentOrder,
  addCurrentOrderInfo,
  addCurrentUser,
  clearCurrentOrder,
} from 'src/redux/action/currentOrder.action';
import { toastAlert } from 'src/utils/toastAlert';
import { v4 as uuidv4 } from 'uuid';
import Service from 'src/setup/network';
import toast from 'react-hot-toast';
import { CurrencyAmount } from 'src/containers/currency';

const theme = createTheme({
  components: {
    MuiSlider: {
      styleOverrides: {
        thumb: {
          width: '50px',
          height: '50px',
          backgroundColor: 'transparent',
          backgroundImage: `url(${fire})`,
          boxShadow: 'none',
          transition: 'none',
          border: 'none',
          '::before': {
            boxShadow: 'none',
            transition: 'none',
          },
          ':hover': {
            boxShadow: 'none',
            transition: 'none',
          },
          '&.Mui-active': {
            boxShadow: 'none',
          },
        },
        rail: {
          backgroundColor: '#E7302A',
        },
        track: {
          backgroundColor: '#E7302A',
          border: 'none',
        },
        mark: {
          backgroundColor: 'white',
        },
      },
    },
  },
});

const ItemsModal: React.FC<any> = ({
  showModal,
  handleClose,
  items,
  item,
  type,
  tableId,
  tableName,
  id,
  fetchTableDataBackOffice
}) => {
  const symbol = useSelector(
    ({ currencyData }: RootState) => currencyData.myCurrencyData?.symbol ?? 'Rs.'
  );
  const [quantity, setQuantity] = useState(1);
  const [spiceLevel, setSpiceLevel] = useState(0);
  const [message, setMessage] = useState('');
  const [modifiers, setModifiers] = useState<any>([]);
  const [errorMsg, setErrorMsg] = useState('');
  const dispatch = useDispatch();

  const { orders, currentUser, deliveryCharge, orderInfo, taxes } = useSelector(
    ({ currentOrderData }: RootState) => currentOrderData
  );

  useEffect(() => {
    if (items.modifiers.length > 0) {
      const newSelectedModifiers = items.modifiers.map((mod: any) => {
        const optionsWithSelected = mod.options.map((op: any) => ({
          id: op.id,
          price: op.price,
          name: op.name,
          hasSelected: false,
        }));
        return {
          id: mod.id,
          name: mod.name,
          isRequired: mod.isRequired,
          requiredSelections: mod.requiredSelections,
          options: optionsWithSelected,
        };
      });
      setModifiers(newSelectedModifiers);
    }
  }, [items]);

  const handleCheckboxChange = (optionId: string) => {
    const newModifiers = [...modifiers];

    for (const mod of newModifiers) {
      const option = mod.options.find((op: any) => op.id === optionId);
      if (option) {
        option.hasSelected = !option.hasSelected;
        break;
      }
    }
    setModifiers(newModifiers);
  };

  const handleChangeSlide = (e: any, newVal: any) => {
    setSpiceLevel(newVal);
  };

  const checkModifiersValidity = () => {
    let modifieresValid = true;
    // check if modifieres conditions are met
    modifiers?.forEach((element: any, i: number) => {
      let totalSelected = 0;
      const requiredSelectedMin = element.isRequired ? 1 : 0;
      const requiredSelectedMax = element.requiredSelections;
      element.options?.forEach((el: any) => {
        if (el.hasSelected) totalSelected++;
      });
      if (!element.isRequired) return;
      else if (totalSelected < requiredSelectedMin) {
        setErrorMsg(
          `Please select atleast ${requiredSelectedMin} option${
            requiredSelectedMin > 1 ? 's' : ''
          } in ${element.name}`
        );
        modifieresValid = false;
        return;
      } else if (totalSelected > requiredSelectedMax) {
        setErrorMsg(
          `Please select only ${requiredSelectedMax} options in ${element.name}`
        );
        modifieresValid = false;
        return;
      }
    });
    return modifieresValid;
  };

  const addOrder = async () => {
    const validMods = await checkModifiersValidity();
    if (!validMods) return;
    let randomId = '';

    const userInfo = JSON.parse(window.localStorage.getItem('userInfo') || '');
    const data = {
      businessId: userInfo?.businessInfo?.id,
      type: type,
      tableId: tableId,
      tableName: tableName,
      userId:
        orders?.origin === 'website'
          ? orders.customer?.id || ''
          : currentUser
          ? currentUser.id || ''
          : null,
      origin: orders.origin || 'pos_device',
      customer: orders.customer || null,
      deliveryCharge: type === 'delivery' ? deliveryCharge : 0,
      promo: null,
      items: [
        {
          itemId: items.id,
          name: items.name,
          variantId: item.id,
          hasVariants: item.hasVariants,
          variantName: item.name,
          identifierId: randomId || null,
          quantity: quantity,
          spiceLevel: items.hasSpiceLevel ? getSpiceText(spiceLevel) : null,
          selectedModifiers: modifiers,
          price: item.discountedPrice,
          note: message,
        },
      ],
    };
    dispatch(addCurrentOrder({ ...data }));
    let taxesTotal = 0;
    const modsPrice = modifiers
      .flatMap((modifier: any) => modifier.options)
      .filter((option: any) => option.hasSelected)
      .reduce((sum: number, option: any) => sum + option.price, 0);
    const subTotal =
      (orderInfo?.subTotal || 0) + quantity * item.discountedPrice + modsPrice;

    taxes.map((each, i) => {
      if (each.type === 'percent') {
        taxesTotal += taxesTotal + (subTotal * each.value) / 100;
      } else taxesTotal += subTotal + each.value;
    });

    dispatch(
      addCurrentOrderInfo({
        ...orderInfo,
        subTotal: subTotal,
        total: subTotal + taxesTotal,
      })
    );
    toastAlert('success', `Added item successfully`);
    handleClose();
    setQuantity(1);
    setMessage('');
    setSpiceLevel(0);
    setModifiers([]);

    const a = {
      ...data,
    };
    const res: any = await Service.post('/orders/openCheck/add', a);
    const response = res?.data?.combinedOrders?.orderInfo?.items;
    const ab = {
      businessId: userInfo?.businessInfo?.id,
      type: orders.type,
      tableId: tableId,
      tableName: tableName,
      userId: null,
      origin: 'pos_device',
      promo: null,
      deliveryCharge: 0,
      items:
        response.length > 0
          ? response.map((item: any) => {
              return {
                ...item,
                name: item.itemName,
                identifierId: item.id,
                deliveryCharge: 0,
                selectedModifiers: item.allModifiers.map((modifier: any) => {
                  const optionsWithSelected = modifier.options.map(
                    (option: any) => {
                      const optionInModifiers = item.modifiers
                        .find((mod: any) => mod.id === modifier.id)
                        ?.options.find((op: any) => op.id === option.id);
                      return {
                        id: option.id,
                        price: option.price,
                        name: option.name,
                        hasSelected: !!optionInModifiers,
                      };
                    }
                  );
                  return {
                    id: modifier.id,
                    name: modifier.name,
                    options: optionsWithSelected,
                  };
                }),
                note: item?.note || '',
              };
            })
          : [],
    };
    if (response.length > 0) {
      dispatch(clearCurrentOrder());
      dispatch(addCurrentOrder({ ...ab }));
      if (currentUser) {
        const currentUserWithDefaults = {
          ...currentUser,
          email: currentUser.email ?? null,
          image: currentUser.image ?? null,
        };

        dispatch(addCurrentUser(currentUserWithDefaults));
      }
    }
    dispatch(addCurrentOrderInfo(res?.data?.combinedOrders?.orderInfo));
    if(fetchTableDataBackOffice) fetchTableDataBackOffice();
  };

  return (
    <>
      {showModal ? (
        <ThemeProvider theme={theme}>
          <Modal
            title={item?.name}
            onClick={handleClose}
            className="w-[388px] z-[9988]"
            hideCross={true}
            crossContent={`${symbol} ${item.discountedPrice}`}
          >
            <div className="mt-4">
              <div className="flex flex-col items-center justify-end mt-2 ">
                <div className="relative flex flex-col gap-4 items-center w-[338px] h-fit mb-4 ">
                  <div className="flex flex-col items-start justify-center gap-[5px] w-full ">
                    <div className="font-Outfit font-semibold text-[14px] leading-[21px]">
                      Quantity
                    </div>
                    <div className="flex items-center gap-[5px]">
                      <div
                        className="w-[25px] h-[25px] rounded-[4px] bg-[#949CC0] flex justify-center items-center"
                        onClick={() => {
                          if (quantity === 1) {
                            return;
                          }
                          setQuantity((q) => q - 1);
                        }}
                      >
                        <BiMinus color="FFF" />
                      </div>
                      <div className="w-[25px] h-[25px] bg-[#F1F1F1] flex justify-center items-center font-Sarabun font-normal text-xs">
                        {quantity}
                      </div>
                      <div
                        className="w-[25px] h-[25px] rounded-[4px] bg-[#949CC0] flex justify-center items-center"
                        onClick={() => {
                          setQuantity((q) => q + 1);
                        }}
                      >
                        <BsPlusLg color="FFF" />
                      </div>
                    </div>
                  </div>

                  {modifiers.length > 0 && (
                    <div className="flex flex-col items-start justify-center gap-[5px] w-full h-fit  ">
                      <div className="font-Outfit font-semibold text-[14px] leading-[21px] ">
                        Additions
                      </div>

                      {modifiers?.map((mod: any) => (
                        <div
                          className="w-full flex flex-col gap-[9.5px] min-h-[23px] h-fit justify-center items-center "
                          key={mod.id}
                        >
                          <span className="mt-1 ml-3 font-semibold text-light-grey self-start">
                            {mod.name}
                          </span>
                          {mod.options.map((op: any) => (
                            <div
                              key={op.id}
                              className="w-full flex justify-between items-center h-[21px] font-Outfit font-normal text-[14px] leading-[21px] "
                            >
                              <div className="flex items-center">
                                <Checkbox
                                  sx={{
                                    color: '#CACFE3',
                                    '&.Mui-checked': {
                                      color: '#E7302A',
                                    },
                                  }}
                                  checked={op.hasSelected}
                                  onChange={() => handleCheckboxChange(op.id)}
                                />
                                <div className=" text-[#2B2B2B]">{op.name}</div>
                              </div>
                              <div className="text-[#000000]">
                                <CurrencyAmount amount={op.price}/>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}

                  {items?.hasSpiceLevel && (
                    <div className="w-full flex flex-col gap-[9.5px] min-h-[23px] h-fit justify-center ">
                      <div className="font-Outfit font-semibold text-[14px] leading-[21px] ">
                        Spice level
                      </div>
                      <p className="font-Outfit font-semibold text-base text-warning text-center ">
                        {getSpiceText(spiceLevel)}
                      </p>
                      <SliderMUI
                        defaultValue={[0]}
                        value={spiceLevel}
                        aria-label="modifier"
                        step={null}
                        onChange={handleChangeSlide}
                        valueLabelDisplay="off"
                        marks={[0, 28, 66, 100].map((each) => ({
                          value: each,
                          label: '',
                        }))}
                      />
                    </div>
                  )}
                  <div className="w-full flex flex-col gap-[9.5px] min-h-[23px] h-fit justify-center ">
                    <div className="my-1 flex flex-col">
                      <label
                        htmlFor="noteCashAdd"
                        className="text-sm  text-[#2B2B2B] font-Outfit "
                      >
                        Message
                      </label>
                      <textarea
                        className="border-[1px] border-light-gray rounded-xl px-1 py-2 focus:border-[1px] focus:border-primary-200 mt-3 focus:shadow-none focus:ring-0 focus:outline-none text-sm  text-[#2B2B2B] font-Outfit"
                        rows={4}
                        cols={42}
                        name="noteCashAdd"
                        onChange={(e) => {
                          setMessage(e.target.value.trim());
                        }}
                      />
                    </div>
                  </div>
                </div>

                {errorMsg && (
                  <p className="text-center text-warning font-semibold mb-2">
                    {errorMsg}
                  </p>
                )}

                <div className="flex flex-row justify-end gap-6 items-center w-full">
                  <div
                    className="font-Sarabun font-semibold text-base text-primary cursor-pointer"
                    onClick={handleClose}
                  >
                    Cancel
                  </div>
                  <Button
                    text="Add to Order"
                    variant="primary"
                    type="submit"
                    className=""
                    onClick={addOrder}
                  />
                </div>
              </div>
            </div>
          </Modal>
        </ThemeProvider>
      ) : (
        <></>
      )}
    </>
  );
};

export default ItemsModal;
